import type { ForwardedRef } from 'react';
import { Fragment, forwardRef } from 'react';

import { c } from 'ttag';

import { useOrganization } from '@proton/account/organization/hooks';
import { useUser } from '@proton/account/user/hooks';
import SimpleDropdown from '@proton/components/components/dropdown/SimpleDropdown';
import Icon from '@proton/components/components/icon/Icon';
import type { AppLinkProps } from '@proton/components/components/link/AppLink';
import useConfig from '@proton/components/hooks/useConfig';
import { getAvailableApps } from '@proton/shared/lib/apps/apps';
import type { APP_NAMES } from '@proton/shared/lib/constants';
import { BRAND_NAME } from '@proton/shared/lib/constants';
import { isElectronMail, isElectronOnInboxApps, isElectronOnMac } from '@proton/shared/lib/helpers/desktop';
import type { OrganizationExtended, UserModel } from '@proton/shared/lib/interfaces';
import { useFlag } from '@proton/unleash';

import { InboxDesktopAppSwitcher } from '../desktop/InboxDesktopAppSwitcher';
import ProductIcon from './ProductIcon';
import ProductLink from './ProductLink';

interface AppsDropdownProps {
    onDropdownClick?: () => void;
    app?: APP_NAMES;
    user?: UserModel;
    organization?: OrganizationExtended;
    title?: string;
    reloadDocument?: AppLinkProps['reloadDocument'];
}

const AppsDropdown = forwardRef<HTMLButtonElement, AppsDropdownProps>(
    (
        { onDropdownClick, app, user, organization, title, reloadDocument, ...rest }: AppsDropdownProps,
        ref: ForwardedRef<HTMLButtonElement>
    ) => {
        const { APP_NAME } = useConfig();
        const isDocsHomepageAvailable = useFlag('DriveDocsLandingPageEnabled');
        const isSheetsAvailable = useFlag('DocsSheetsEnabled');
        const isMeetAvailable = useFlag('PMVC2025');

        const availableApps = getAvailableApps({
            context: 'dropdown',
            user,
            organization,
            isDocsHomepageAvailable,
            isSheetsAvailable,
            isMeetAvailable,
        });

        if (availableApps.length <= 1) {
            return null;
        }

        return (
            <SimpleDropdown
                type="button"
                hasCaret={false}
                content={<Icon name="app-switch" size={6} className="apps-dropdown-button-icon shrink-0 no-print" />}
                className="apps-dropdown-button shrink-0"
                dropdownClassName="apps-dropdown rounded-lg"
                originalPlacement="bottom-start"
                title={title ? title : c('Apps dropdown').t`${BRAND_NAME} applications`}
                onClick={onDropdownClick}
                disableDefaultArrowNavigation
                {...rest}
                ref={ref}
                as="button"
            >
                <ul
                    className="unstyled my-0 p-4"
                    style={{ '--apps-dropdown-repeat': isElectronMail || availableApps.length <= 2 ? '2' : '3' }}
                >
                    {availableApps.map((appToLinkTo) => {
                        const current = app && appToLinkTo === app;

                        return (
                            <Fragment key={appToLinkTo}>
                                <li className="dropdown-item apps-dropdown-item" data-testid="apps-dropdown-item">
                                    <ProductLink
                                        ownerApp={APP_NAME}
                                        app={app}
                                        user={user}
                                        appToLinkTo={appToLinkTo}
                                        className="text-center text-no-decoration outline-none--at-all apps-dropdown-link flex flex-column items-center"
                                        current={current}
                                        reloadDocument={reloadDocument}
                                        // The same app opens in the same window, other apps in new windows
                                        target={APP_NAME === appToLinkTo ? '_self' : '_blank'}
                                    >
                                        <ProductIcon appToLinkTo={appToLinkTo} current={current} />
                                    </ProductLink>
                                </li>
                            </Fragment>
                        );
                    })}
                </ul>
            </SimpleDropdown>
        );
    }
);

AppsDropdown.displayName = 'AppsDropdown';

export const UnAuthenticatedAppsDropdown = AppsDropdown;

const AuthenticatedAppsDropdown = forwardRef<HTMLButtonElement, AppsDropdownProps>(
    (props: AppsDropdownProps, ref: ForwardedRef<HTMLButtonElement>) => {
        const [user] = useUser();
        const [organization] = useOrganization();
        const { APP_NAME } = useConfig();
        const isInboxCustomAppSwitcher = useFlag('InboxDesktopWinLinNewAppSwitcher');

        // The app swicher on Mail, Calendar and account desktop application is different
        if (isElectronOnInboxApps(APP_NAME) && (isElectronOnMac || (isInboxCustomAppSwitcher && isElectronMail))) {
            return <InboxDesktopAppSwitcher appToLinkTo={props.app} />;
        }

        return <AppsDropdown ref={ref} {...props} user={user} organization={organization} />;
    }
);

AuthenticatedAppsDropdown.displayName = 'AuthenticatedAppsDropdown';

export default AuthenticatedAppsDropdown;
